import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { resolveModifiers } from '@shripos/shared/billing';
import { Alert, Button, Dialog, Input, cn } from '@shripos/ui';
import { FoodTypeMark } from '../../components/FoodTypeMark.jsx';
import { rupees } from '../../lib/money.js';
import { kitchenOf, useSession } from '../../stores/session.js';

/** Choose size, add-ons (min/max rules), quantity and a kitchen note before adding to the cart. */
export function ItemOptionsDialog({ item, maps, onAdd, onClose }) {
  const { t } = useTranslation();
  const kitchenOrders = useSession((st) => kitchenOf(st.me).orders);
  const variants = item.variants ?? [];
  const groups = (item.modifierGroupIds ?? []).map((id) => maps.modifierGroupsById.get(id)).filter(Boolean);
  const [variantId, setVariantId] = useState(() => variants.find((v) => !v.outOfStock)?.id ?? null);
  const [selected, setSelected] = useState([]);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');

  const check = useMemo(() => resolveModifiers(groups, selected), [groups, selected]);
  const base = variants.length ? (variants.find((v) => v.id === variantId)?.pricePaise ?? 0) : item.pricePaise;
  const unit = base + (check.ok ? check.selected.reduce((s, m) => s + m.pricePaise, 0) : 0);

  const toggle = (group, optionId) => {
    setSelected((cur) => {
      const inGroup = group.options.map((o) => o.id);
      const has = cur.includes(optionId);
      if (has) return cur.filter((x) => x !== optionId);
      if (group.maxSelect === 1) return [...cur.filter((x) => !inGroup.includes(x)), optionId];
      const count = cur.filter((x) => inGroup.includes(x)).length;
      return count >= group.maxSelect ? cur : [...cur, optionId];
    });
  };

  const canAdd = (!variants.length || variantId) && check.ok;
  const add = () => {
    onAdd({ itemId: item.id, variantId: variantId ?? null, modifierOptionIds: selected, qty, note: note.trim() });
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <FoodTypeMark type={item.foodType} /> {item.name}
        </span>
      }
      footer={
        <div className="flex w-full items-center gap-3">
          <div className="flex items-center rounded-xl ring-1 ring-slate-300">
            <button
              className="p-3"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              aria-label={t('pos.decreaseQty', 'Decrease')}
            >
              <Minus className="size-4" />
            </button>
            <span className="w-8 text-center font-semibold tabular-nums">{qty}</span>
            <button
              className="p-3"
              onClick={() => setQty((q) => Math.min(999, q + 1))}
              aria-label={t('pos.increaseQty', 'Increase')}
            >
              <Plus className="size-4" />
            </button>
          </div>
          <Button size="lg" className="flex-1" disabled={!canAdd} onClick={add} data-autofocus>
            {t('pos.addToOrder', 'Add')} · {rupees(unit * qty)}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {variants.length > 0 && (
          <section>
            <p className="mb-2 text-sm font-semibold text-slate-700">{t('pos.size', 'Size')}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {variants.map((v) => (
                <button
                  key={v.id}
                  disabled={v.outOfStock}
                  onClick={() => setVariantId(v.id)}
                  className={cn(
                    'rounded-xl p-3 text-left ring-1 transition disabled:opacity-40',
                    variantId === v.id ? 'bg-brand-50 ring-2 ring-brand-500' : 'ring-slate-200 hover:bg-slate-50',
                  )}
                >
                  <span className="block font-medium">{v.name}</span>
                  <span className="text-sm text-slate-600">
                    {v.outOfStock ? t('pos.outOfStock', 'Out of stock') : rupees(v.pricePaise)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
        {groups.map((g) => (
          <section key={g.id}>
            <p className="mb-2 text-sm font-semibold text-slate-700">
              {g.name}
              <span className="ml-2 font-normal text-slate-500">
                {g.minSelect > 0
                  ? t('pos.choose', 'choose {{n}}', {
                      n: g.minSelect === g.maxSelect ? g.minSelect : `${g.minSelect}–${g.maxSelect}`,
                    })
                  : t('pos.upTo', 'up to {{n}}', { n: g.maxSelect })}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {g.options
                .filter((o) => o.isActive)
                .map((o) => {
                  const on = selected.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      onClick={() => toggle(g, o.id)}
                      className={cn(
                        'rounded-xl p-3 text-left ring-1 transition',
                        on ? 'bg-brand-50 ring-2 ring-brand-500' : 'ring-slate-200 hover:bg-slate-50',
                      )}
                    >
                      <span className="block text-sm font-medium">{o.name}</span>
                      <span className="text-xs text-slate-600">
                        {o.pricePaise ? `+${rupees(o.pricePaise)}` : t('pos.free', 'Free')}
                      </span>
                    </button>
                  );
                })}
            </div>
          </section>
        ))}
        {!check.ok && selected.length > 0 && <Alert tone="warning">{check.error}</Alert>}
        <section>
          <p className="mb-2 text-sm font-semibold text-slate-700">
            {kitchenOrders ? t('pos.kitchenNote', 'Note for the kitchen') : t('pos.lineNote', 'Note')}
          </p>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 140))}
            placeholder={t('pos.noteExample', 'e.g. less spicy, no onion')}
          />
        </section>
      </div>
    </Dialog>
  );
}
